/**
 * Gallery-only chrome that names the vocabulary an agent reuses: the element
 * types, the layouts or the archetype a specimen demonstrates. Production
 * routes never render it — owners do not see machine names.
 */
import React from 'react';
import { FmStack, FmText } from './FmComposition';
import './FmGalleryIdentity.css';

export interface FmGalleryIdentityProps {
  /** Which closed vocabulary the names come from. */
  vocabulary: 'element' | 'layout' | 'archetype' | 'surface' | 'tone';
  /** Machine names from that vocabulary (`keyFigures`, `split`, `FmProductPage`, `glass-deep`, `paper`). */
  names: readonly string[];
  /** Human name of this specimen. */
  label: string;
  /** One line on what to reuse it for. */
  useFor: string;
  'data-testid': string;
}

const VOCABULARY_LABEL: Record<FmGalleryIdentityProps['vocabulary'], [string, string]> = {
  element: ['element', 'elements'],
  layout: ['layout', 'layouts'],
  archetype: ['archetype', 'archetypes'],
  surface: ['surface', 'surfaces'],
  tone: ['tone', 'tones'],
};

export const FmGalleryIdentity: React.FC<FmGalleryIdentityProps> = ({
  vocabulary,
  names,
  label,
  useFor,
  'data-testid': testId,
}) => {
  const [singular, plural] = VOCABULARY_LABEL[vocabulary];
  return (
    <FmStack
      gap="xs"
      className="fm-gallery-identity"
      data-testid={testId}
      data-vocabulary={vocabulary}
      data-names={names.join(' ') || undefined}
    >
      {names.length > 0 ? (
        <FmText size="fine" tone="muted">
          {`${names.length > 1 ? plural : singular} `}
          <FmText as="span" size="fine" mono>
            {names.join(' · ')}
          </FmText>
        </FmText>
      ) : null}
      <FmText size="caption">{label}</FmText>
      <FmText size="caption" tone="muted">
        {useFor}
      </FmText>
    </FmStack>
  );
};

FmGalleryIdentity.displayName = 'FmGalleryIdentity';
